/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useAiProvidersColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for AI providers column definitions with custom renderers.
────────────────────────────────────────────────────────────────*/

import { useMemo } from 'react';
import { Bot } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import type { AiProvider } from '../api/aiProvidersApi';

export function useAiProvidersColumns() {
  const { t, refreshKey, i18n } = useI18n();

  const availableColumns = useMemo(() => [
    { key: 'name', label: t('aiProviders.fields.name.label'), visible: true, sortable: true },
    { key: 'provider', label: t('aiProviders.fields.provider.label'), visible: true, sortable: true },
    { key: 'api_key_type', label: t('aiProviders.fields.genericKeyTypeLabel'), visible: true, sortable: true },
    { key: 'status', label: t('aiProviders.fields.status.label'), visible: true, sortable: true },
    { key: 'created_at', label: t('aiProviders.fields.createdAt.label'), visible: false, sortable: true },
    { key: 'actions', label: t('common.actions_title'), visible: true, sortable: false }
  ], [t, i18n.language, refreshKey]);

  const getColumnRenderer = (key: string) => {
    switch (key) {
      case 'provider':
        return (value: unknown) => (
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{String(value) || 'Unknown'}</span>
          </div>
        );

      case 'name':
        return (value: unknown) => (
          <span className="font-medium">{String(value) || 'No name'}</span>
        );

      case 'api_key_type':
        return (value: unknown) => (
          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 uppercase tracking-tight font-medium">
            {String(value) || 'Default'}
          </span>
        );

      case 'status':
        return (value: unknown) => (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${value
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            }`}>
            {value ? t('common.status.active') : t('common.status.inactive')}
          </span>
        );

      case 'created_at':
        return (value: unknown) => (
          <div className="text-sm text-muted-foreground">
            {new Date(value as string | number).toLocaleDateString()}
          </div>
        );

      default:
        return undefined;
    }
  };

  return {
    availableColumns,
    getColumnRenderer
  };
}

